import type { MicrosoftLicence } from './microsoftLicencePricing';

/**
 * A Microsoft catalogue product sells one Microsoft licence, identified by the GUID that
 * Microsoft Graph returns as `subscribedSku.skuId`. Only a link the partner has saved counts.
 * A product's name never links it: a name can match the wrong SKU, and two licences can share one.
 */
export interface MicrosoftLinkable {
  kind?: string;
  domain?: string;
  name?: string;
  vendorOrBrand?: string;
  vendorPreset?: string;
  microsoftSkuId?: string;
}

export function findMicrosoftLicence(
  licences: readonly MicrosoftLicence[],
  skuId: string | undefined,
): MicrosoftLicence | undefined {
  const wanted = skuId?.trim().toLowerCase();
  return wanted ? licences.find((licence) => licence.skuId === wanted) : undefined;
}

export function isMicrosoftPlan(product: MicrosoftLinkable): boolean {
  const software = product.kind === 'software' || product.domain === 'software';
  return software && product.vendorPreset === 'microsoft';
}

/** The licence this product sells: the saved link on a Microsoft plan, nothing otherwise. */
export function resolvedMicrosoftSkuId(product: MicrosoftLinkable): string | undefined {
  if (!isMicrosoftPlan(product)) return undefined;
  return product.microsoftSkuId?.trim() || undefined;
}

export function isUnlinkedMicrosoftPlan(product: MicrosoftLinkable): boolean {
  return isMicrosoftPlan(product) && !resolvedMicrosoftSkuId(product);
}

/** What to persist: only a Microsoft plan sells a Microsoft licence. */
export function microsoftSkuIdToSave(product: MicrosoftLinkable): string | undefined {
  return resolvedMicrosoftSkuId(product);
}

function normalise(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/**
 * Licences whose Microsoft name is the product's name, read with or without the vendor in
 * front, so "365 Business Premium" by Microsoft suggests Microsoft 365 Business Premium.
 * A suggestion is only offered; the partner picks one to link it.
 */
export function suggestMicrosoftLicences(
  licences: readonly MicrosoftLicence[],
  productName: string,
  vendor?: string,
): MicrosoftLicence[] {
  const name = normalise(productName);
  if (!name) return [];
  const qualified = vendor ? normalise(`${vendor} ${productName}`) : name;
  return licences.filter((licence) => {
    const official = normalise(licence.name);
    return official === name || official === qualified;
  });
}

/** Case-insensitive match on the name, String ID or GUID; every word of the query must appear. */
export function searchMicrosoftLicences(licences: readonly MicrosoftLicence[], query: string): MicrosoftLicence[] {
  const words = normalise(query).split(' ').filter(Boolean);
  if (words.length === 0) return [...licences];
  return licences.filter((licence) => {
    const haystack = normalise(`${licence.name} ${licence.skuPartNumber} ${licence.skuId}`);
    return words.every((word) => haystack.includes(word));
  });
}
